import {
    ADGroups,
    CaseFileStaticItems,
    CustomLogicAssertionDescriptors,
} from '../../../utilities/Constants';
import { ICaseAssertionModel, ICaseModel } from '../../../interfaces/ICase';
import {
    ILookupModel,
    IMultiSelectOption,
    IStaticItemLookupModel,
} from '../../../interfaces/ILookup';
import { ISameCaseEasyUpdateCaseModel, ISameCaseEasyUpdateModel } from './ISameCaseEasyUpdateModel';
import { cloneDeep, isEmpty } from 'lodash';
import { showErrorToast, showSuccessToast } from '../../../shared/ToastUtils';
import { useEffect, useState } from 'react';

import CaseDataCustomLogicHelper from '../../../utilities/CaseDataCustomLogicHelper';
import CaseHelper from '../../../utilities/CaseHelper';
import { CheckmateDialog } from '../../../components/shared/dialog';
import { DocumentTitle } from '../../../components/shared/DocumentTitle';
import { FieldSelector } from './FieldSelector';
import { IAssertionDescriptorModel } from '../../../interfaces/IAssertionDescriptor';
import { IValidation } from '../../../interfaces/IError';
import { Loader } from '../../../components/shared/Loader';
import { LoadingButton } from '../../../shared/buttons/LoadingButton';
import { PageWithAffixedHeader } from '../../../shared/PageWithAffixedHeader';
import { SelectedFieldRow } from './SelectedFieldRow';
import Sort from '../../../stores/Sort';
import { Stack } from 'react-bootstrap';
import UIHelper from '../../../utilities/UIHelper';
import { UnsavedChangesBlocker } from '../../../shared/UnsavedChangesBlocker';
import ValidateUtils from '../../../shared/validations';
import { useGetCenterCaseAssertionDescriptors } from '../../../shared/react-query-hooks/useGetCenterCaseAssertionDescriptors';
import { useGetCenterStaticItems } from '../../../shared/react-query-hooks/useGetCenterStaticItems';
import { useGetSameCaseEasyUpdate } from './useGetSameCaseEasyUpdate';
import { useMainContainerRef } from '../../../contexts/MainContainerRefContext';
import { useSaveSameCaseEasyUpdateMutation } from './useSaveSameCaseEasyUpdateMutation';

const getAdsWithErrorsThatAreNotVisible = (
    adValidation: IAssertionDescriptorValidation,
    selectedFields: IMultiSelectOption[]
) => {
    const selectedGuids = new Set(selectedFields.map((f) => f.guid));
    const result = new Set<string>();

    for (const zone of Object.values(adValidation)) {
        for (const assertionDescriptorGuid of Object.keys(zone)) {
            if (!selectedGuids.has(assertionDescriptorGuid)) {
                result.add(assertionDescriptorGuid);
            }
        }
    }

    return Array.from(result);
};

const getSIsWithErrorsThatAreNotVisible = (
    siValidation: IStaticItemValidation,
    selectedFields: IMultiSelectOption[]
) => {
    const selectedIds = new Set(selectedFields.map((f) => f.id));
    const result = new Set<number>();

    for (const zone of Object.values(siValidation)) {
        for (const staticItemId of Object.keys(zone)) {
            const staticItemIdInt = parseInt(staticItemId);
            if (!selectedIds.has(staticItemIdInt)) {
                result.add(staticItemIdInt);
            }
        }
    }

    return Array.from(result);
};

export interface IAssertionDescriptorValidation {
    [zoneGuid: string]: {
        [assertionDescriptorGuid: string]: string[];
    };
}
export interface IStaticItemValidation {
    [zoneGuid: string]: {
        [staticItemId: number]: string[];
    };
}

export function SameCaseEasyUpdate(props: {
    defaultSelectedFields?: IMultiSelectOption[];
    isRoot?: boolean;
    onCancel?: () => void;
    onComplete?: (sameCase: ISameCaseEasyUpdateModel | undefined) => void;
    onSelectedFieldsChange?: (selectedFields: IMultiSelectOption[]) => void;
    sameCaseGuid: string | undefined;
}) {
    const mainContainerDivRef = useMainContainerRef();
    const { sameCaseGuid } = props;
    const saveMutation = useSaveSameCaseEasyUpdateMutation();

    const staticItemsApi = useGetCenterStaticItems(sameCaseGuid);
    const assertionDescriptorsApi = useGetCenterCaseAssertionDescriptors(sameCaseGuid);
    const caseGroupEasyUpdateApi = useGetSameCaseEasyUpdate(sameCaseGuid);

    const [sameCaseEasyUpdateModel, setSameCaseEasyUpdateModel] =
        useState<ISameCaseEasyUpdateModel>();
    const [originalSameCaseEasyUpdateModel, setOriginalSameCaseEasyUpdateModel] =
        useState<ISameCaseEasyUpdateModel>();
    const [zonesWithErrors, setZonesWithErrors] = useState<string[]>([]);
    const [availableFields, setAvailableFields] = useState<IMultiSelectOption[]>([]);
    const [selectedFields, setSelectedFields] = useState<IMultiSelectOption[]>(
        props.defaultSelectedFields ?? []
    );
    const [validation, setValidation] = useState<IValidation>({});
    const [systemGeneratedIds, setSystemGeneratedIds] = useState<string[]>([]);
    const [adGroupFieldToDelete, setAdGroupFieldToDelete] = useState<
        IMultiSelectOption | undefined
    >(undefined);
    const [changesWereMade, setChangesWereMade] = useState<boolean>(false);
    const [showUnsavedChangesOnCancelDialog, setShowUnsavedChangesOnCancelDialog] =
        useState<boolean>(false);
    const [assertionDescriptorValidation, setAssertionDescriptorValidation] =
        useState<IAssertionDescriptorValidation>({});
    const [staticItemValidation, setStaticItemValidation] = useState<IStaticItemValidation>({});
    const addStaticItemsToAvailableFields = (availableFields: IMultiSelectOption[]) => {
        let availableFieldsCopy = cloneDeep(availableFields);
        const staticItems = staticItemsApi.data ?? [];

        const staticItemFilter = (x: IStaticItemLookupModel) =>
            x.displayItemTypeId === 1 && x.id !== CaseFileStaticItems.PackageSettlement.Id;

        availableFieldsCopy = availableFieldsCopy.concat(
            staticItems.filter(staticItemFilter).map((item: ILookupModel) => ({
                id: item.id,
                label: item.alternateDisplayName || item.displayName || item.name || '',
                value: item.name || '',
            }))
        );

        return availableFieldsCopy;
    };

    const addADsToAvailableFields = (availableFields: IMultiSelectOption[]) => {
        const availableFieldsCopy = cloneDeep(availableFields);
        const assertionDescriptors = assertionDescriptorsApi.data ?? [];

        const getIsReadOnlyPerConfig = (assertionDescriptorGuid: string | undefined) => {
            return !!CustomLogicAssertionDescriptors.find(
                (customLogicAD) =>
                    customLogicAD.Guid.toUpperCase() === assertionDescriptorGuid?.toUpperCase()
            )?.ReadOnly;
        };

        assertionDescriptors.forEach((item: IAssertionDescriptorModel) => {
            const forcedReadOnly = getIsReadOnlyPerConfig(item.guid);

            if (!forcedReadOnly) {
                availableFieldsCopy.push({
                    id: -1,
                    label: item.alternateDisplayName || item.displayName!,
                    value: item.name!,
                    guid: item.guid,
                });
            }
        });

        return availableFieldsCopy;
    };

    const loadAvailableFields = () => {
        let availableFields: IMultiSelectOption[] = [];
        availableFields = addStaticItemsToAvailableFields(availableFields);
        availableFields = addADsToAvailableFields(availableFields);
        availableFields = availableFields.sort(Sort.compareValues('label'));
        setAvailableFields(availableFields);
    };

    const getUpdatedEasyUpdateFields = (
        currentCase: ICaseModel,
        easyUpdateFieldsSelected: IMultiSelectOption[]
    ): IMultiSelectOption[] => {
        const assertionDescriptors = assertionDescriptorsApi.data ?? [];
        const staticItems = staticItemsApi.data ?? [];

        const additionalFields = CaseHelper.getAdditionalCaseAssertionFields(
            currentCase,
            assertionDescriptors
        );

        const autoPopulatedGuids = additionalFields.map(
            (field) => field.assertionDescriptorGuid ?? ''
        );

        additionalFields.forEach((currentCaseAssertion) => {
            const foundAD = assertionDescriptors.find(
                (ad) => ad.guid === currentCaseAssertion.assertionDescriptorGuid
            );

            const shouldAddField =
                foundAD &&
                !easyUpdateFieldsSelected.some(
                    (x) =>
                        x.guid?.toUpperCase() ===
                        currentCaseAssertion.assertionDescriptorGuid?.toUpperCase()
                );

            if (shouldAddField) {
                easyUpdateFieldsSelected.push({
                    id: Math.round(Math.random() * 1000000),
                    label:
                        foundAD.alternateDisplayName ?? foundAD.displayName ?? foundAD.name ?? '',
                    value: foundAD.name ?? '',
                    guid: currentCaseAssertion.assertionDescriptorGuid,
                });
            }
        });

        const needToAddCaseStatus =
            additionalFields.length > 0 &&
            availableFields.some((field) => field.id === CaseFileStaticItems.CaseStatus.Id) &&
            !easyUpdateFieldsSelected.some((x) => x.id === CaseFileStaticItems.CaseStatus.Id);

        if (needToAddCaseStatus) {
            easyUpdateFieldsSelected.push({
                id: CaseFileStaticItems.CaseStatus.Id,
                label: CaseHelper.getCaseFileStaticItemDisplayName(
                    staticItems,
                    CaseFileStaticItems.CaseStatus.Id
                ),
                value: UIHelper.getName(staticItems, CaseFileStaticItems.CaseStatus.Id),
            });
        }

        setSystemGeneratedIds(autoPopulatedGuids);

        ADGroups.forEach((adGroup) => {
            CaseHelper.handleGroupFieldsForEasyUpdate(
                currentCase,
                easyUpdateFieldsSelected,
                adGroup,
                assertionDescriptors
            );
        });

        return easyUpdateFieldsSelected;
    };

    const refreshCaseFieldValue = (currentCase: ICaseModel, item: IMultiSelectOption) => {
        const assertionDescriptors = assertionDescriptorsApi.data ?? [];
        const originalCase = originalSameCaseEasyUpdateModel?.caseModels.find(
            (oc) => oc.guid === currentCase.guid
        );

        if (!originalCase) return;

        const assertionDescriptorMatch = assertionDescriptors.find((x) => x.guid === item.guid);

        if (assertionDescriptorMatch) {
            const currentCaseCaseAssertionMatch = currentCase.caseAssertions?.find(
                (x) => x.assertionDescriptorGuid === assertionDescriptorMatch.guid
            );

            const originalCaseCaseAssertionMatch = originalCase.caseAssertions?.find(
                (x) => x.assertionDescriptorGuid === assertionDescriptorMatch.guid
            );

            if (currentCaseCaseAssertionMatch && originalCaseCaseAssertionMatch) {
                Object.assign(currentCaseCaseAssertionMatch, originalCaseCaseAssertionMatch);
            } else if (currentCaseCaseAssertionMatch) {
                Object.assign(currentCaseCaseAssertionMatch, {
                    assertionDescriptorValueGuid: undefined,
                    booleanValue: undefined,
                    dateTimeValue: undefined,
                    numericValue: undefined,
                    textValue: undefined,
                    userOverride: undefined,
                } as ICaseAssertionModel);
            }
        } else {
            switch (item.id) {
                case CaseFileStaticItems.CaseNumber.Id:
                    currentCase.caseNumber = originalCase.caseNumber;
                    break;
                case CaseFileStaticItems.UniqueCaseID.Id:
                    currentCase.uniqueCaseId = originalCase.uniqueCaseId;
                    break;
                case CaseFileStaticItems.CaseStatus.Id:
                    currentCase.caseStatus = cloneDeep(originalCase.caseStatus);
                    break;
                case CaseFileStaticItems.CaseManager.Id:
                    currentCase.caseManagerUser = cloneDeep(originalCase.caseManagerUser);
                    break;
                case CaseFileStaticItems.AlternateCaseManager.Id:
                    currentCase.alternateCaseManagerUser = cloneDeep(
                        originalCase.alternateCaseManagerUser
                    );
                    break;
                case CaseFileStaticItems.LocalCounsel.Id:
                    currentCase.defenseCounsels = cloneDeep(originalCase.defenseCounsels);
                    break;
                case CaseFileStaticItems.CasePriority.Id:
                    currentCase.priority = originalCase.priority;
                    break;
            }
        }
    };

    const deleteField = (item: IMultiSelectOption) => {
        // While deleting the case Field from the grid, remove that value from the list of selected fields.
        // Also, set its value in the "currentCase" variable to the original value from the "originalCase" variable so that next time they select the same field, the UI displays the correct value

        const sameCaseEasyUpdateCopy = cloneDeep(sameCaseEasyUpdateModel);
        sameCaseEasyUpdateCopy?.caseModels.forEach((currentCase) => {
            let selectedFieldsCopy: IMultiSelectOption[] = cloneDeep(selectedFields);
            let itemsToRemove = [item];

            const adGroupGuids =
                ADGroups.find((group) => group.includes(item.guid?.toUpperCase() ?? '')) ?? [];

            // If an item is part of an ADGroup, we need to remove all group members as well
            if (adGroupGuids) {
                const adGroupItems = selectedFieldsCopy.filter((field) =>
                    adGroupGuids
                        .filter((guid) => guid !== item.guid?.toUpperCase())
                        .map((guid) => guid.toUpperCase())
                        .includes(field.guid?.toUpperCase() ?? '')
                );
                itemsToRemove = itemsToRemove.concat([...adGroupItems]);
            }

            selectedFieldsCopy = selectedFieldsCopy.filter((field) => {
                if (field.guid) {
                    return !itemsToRemove.some((item) => item.guid === field.guid);
                } else {
                    return !itemsToRemove.some((item) => item.id === field.id);
                }
            });

            if (currentCase) {
                itemsToRemove.forEach((itemToRemove) => {
                    refreshCaseFieldValue(currentCase, itemToRemove);

                    setAssertionDescriptorValidation((prev) => {
                        const validationCopy = cloneDeep(prev);
                        const orgGuid = currentCase.organization?.guid ?? '';

                        if (
                            itemToRemove.guid &&
                            validationCopy[orgGuid] &&
                            itemToRemove.guid in validationCopy[orgGuid]
                        ) {
                            delete validationCopy[orgGuid][itemToRemove.guid];
                        }

                        return validationCopy;
                    });

                    setStaticItemValidation((prev) => {
                        const validationCopy = cloneDeep(prev);
                        const orgGuid = currentCase.organization?.guid ?? '';

                        if (
                            itemToRemove.id &&
                            validationCopy[orgGuid] &&
                            itemToRemove.id in validationCopy[orgGuid]
                        ) {
                            delete validationCopy[orgGuid][itemToRemove.id];
                        }

                        return validationCopy;
                    });
                });
            }

            setSelectedFields(selectedFieldsCopy);
        });

        setSameCaseEasyUpdateModel(sameCaseEasyUpdateCopy);
    };

    const addAssertionDescriptorError = (
        currentErrors: IAssertionDescriptorValidation,
        zoneGuid: string,
        assertionDescriptorGuid: string,
        errorMessage: string
    ) => {
        if (zoneGuid === '') {
            return;
        }

        // Ensure zoneGuid exists
        if (!(zoneGuid in currentErrors)) {
            currentErrors[zoneGuid] = {};
        }

        // Ensure assertionDescriptorGuid exists for this zone
        if (!(assertionDescriptorGuid in currentErrors[zoneGuid])) {
            currentErrors[zoneGuid][assertionDescriptorGuid] = [];
        }

        // Add the error message
        currentErrors[zoneGuid][assertionDescriptorGuid].push(errorMessage);
    };

    const addStaticItemError = (
        currentErrors: IStaticItemValidation,
        zoneGuid: string,
        staticItemId: number,
        errorMessage: string
    ) => {
        if (zoneGuid === '' || staticItemId === 0) {
            return;
        }

        // Ensure zoneGuid exists
        if (!(zoneGuid in currentErrors)) {
            currentErrors[zoneGuid] = {};
        }

        // Ensure staticItemId exists for this zone
        if (!(staticItemId in currentErrors[zoneGuid])) {
            currentErrors[zoneGuid][staticItemId] = [];
        }

        // Add the error message
        currentErrors[zoneGuid][staticItemId].push(errorMessage);
    };

    const validateStaticItems = (model: ISameCaseEasyUpdateModel) => {
        let hasError = false;
        const staticItemValidation = {};

        model.caseModels.forEach((cm) => {
            if (cm.caseNumber?.trim() === '') {
                addStaticItemError(
                    staticItemValidation,
                    cm.organization?.guid ?? '',
                    CaseFileStaticItems.CaseNumber.Id,
                    'Case Number is required'
                );
                hasError = true;
            }

            if ((cm.caseStatus?.status.id ?? 0) === 0) {
                addStaticItemError(
                    staticItemValidation,
                    cm.organization?.guid ?? '',
                    CaseFileStaticItems.CaseStatus.Id,
                    'Status is required'
                );
                hasError = true;
            }
        });

        setStaticItemValidation(staticItemValidation);
        return hasError;
    };

    const validateAssertionDescriptors = (model: ISameCaseEasyUpdateModel) => {
        let hasError = false;
        const assertionDescriptorValidation = {};
        const assertionDescriptors = assertionDescriptorsApi.data ?? [];
        const ssnAssertionDescriptors = assertionDescriptors.filter((ad) =>
            ad.name?.includes('SSN')
        );

        model.caseModels.forEach((cm) => {
            ssnAssertionDescriptors.forEach((ssnAD) => {
                const ssnCaseAssertion = cm.caseAssertions?.find(
                    (ca) => ca.assertionDescriptorGuid === ssnAD?.guid
                );

                if (
                    ssnCaseAssertion?.textValue &&
                    !ValidateUtils.isValidSSN(ssnCaseAssertion.textValue!)
                ) {
                    addAssertionDescriptorError(
                        assertionDescriptorValidation,
                        cm.organization?.guid ?? '',
                        ssnCaseAssertion.assertionDescriptorGuid ?? '',
                        `Invalid ${ssnAD?.displayName}. Enter a valid SSN in the format XXX-XX-XXXX (Use 0's if you only have last 4 of the SSN i.e. 000 - 000 - 1234)`
                    );
                    hasError = true;
                }
            });
        });

        setAssertionDescriptorValidation(assertionDescriptorValidation);
        return hasError;
    };

    const validate = (model: ISameCaseEasyUpdateModel) => {
        const staticItemsHasError = validateStaticItems(model);
        const assertionDescriptorsHasError = validateAssertionDescriptors(model);
        return staticItemsHasError || assertionDescriptorsHasError;
    };

    const handleSaveClick = () => {
        const onlyEditableZones =
            sameCaseEasyUpdateModel?.caseModels.filter((cm) => cm.canEdit) ?? [];

        const payload = {
            ...sameCaseEasyUpdateModel,
            caseModels: onlyEditableZones,
        } as ISameCaseEasyUpdateModel;

        const hasErrors = validate(payload);

        if (hasErrors) {
            return;
        }

        saveMutation
            .mutateAsync(payload)
            .then((updatedModel) => {
                if (updatedModel?.exceptions && updatedModel?.exceptions?.length > 0) {
                    showErrorToast(
                        <span>
                            <span>Update failed for one or more zones:</span>
                            <ul style={{ paddingTop: '0.7rem' }}>
                                {updatedModel.exceptions.map((ex, i) => (
                                    <li key={ex.organizationGuid}>
                                        <strong>{ex.organizationName}</strong> -{' '}
                                        {ex.exceptionMessage}
                                    </li>
                                ))}
                            </ul>
                            <span>
                                The page has been refreshed with the latest data from the server.
                            </span>
                        </span>
                    );
                }
                if (updatedModel?.caseModels && updatedModel?.caseModels.length > 0) {
                    setZonesWithErrors(
                        updatedModel?.exceptions.map((ex) => ex.organizationGuid) ?? []
                    );
                    setChangesWereMade(true);
                    setSameCaseEasyUpdateModel(updatedModel);
                    setOriginalSameCaseEasyUpdateModel(updatedModel);
                    showSuccessToast('Same Case Group saved!');
                }
            })
            .catch((error) => {
                showErrorToast(
                    <span>
                        Saving the Same Case Group failed!
                        {error.errorMessage && <p>{error.errorMessage}</p>}
                    </span>
                );
            });
    };

    const handleClearClick = () => {
        setSameCaseEasyUpdateModel(originalSameCaseEasyUpdateModel);
        setSelectedFields([]);
        setAssertionDescriptorValidation({});
        setStaticItemValidation({});
    };

    const handleDeleteFieldClick = (item: IMultiSelectOption, force?: boolean) => {
        const flatAdGroups = ADGroups.flatMap((group) => group);

        if (flatAdGroups.includes(item.guid?.toUpperCase() ?? '') && !force) {
            setAdGroupFieldToDelete(item);
        } else {
            deleteField(item);
        }
    };

    const handleEasyUpdateFieldsSelected = (optionsList: IMultiSelectOption[]) => {
        const previousFieldsSelected: IMultiSelectOption[] = cloneDeep(selectedFields);
        const currentFieldsSelected: IMultiSelectOption[] = optionsList;
        const itemDeleted = previousFieldsSelected.length > currentFieldsSelected.length;
        const caseGroupEasyUpdateCopy = cloneDeep(sameCaseEasyUpdateModel);

        caseGroupEasyUpdateCopy?.caseModels.forEach((currentCaseCopy) => {
            // For all the fields that are removed in the current iteration, set value from Original case details
            selectedFields.forEach((prevField) => {
                const isFieldInCurrent = optionsList.some(
                    (x) =>
                        x.guid === prevField.guid ||
                        x.value.toUpperCase() === prevField.value.toUpperCase()
                );

                if (!isFieldInCurrent) {
                    refreshCaseFieldValue(currentCaseCopy, prevField);
                }
            });

            const easyUpdateFieldsSelectedUpdated = itemDeleted
                ? optionsList
                : getUpdatedEasyUpdateFields(currentCaseCopy, optionsList);

            // For UI Reasons, always make case Notes the last item in the list
            const notesSectionId = CaseFileStaticItems.NotesSection.Id;

            if (easyUpdateFieldsSelectedUpdated.some((x) => x.id === notesSectionId)) {
                const index = easyUpdateFieldsSelectedUpdated.findIndex(
                    (x) => x.id === notesSectionId
                );
                easyUpdateFieldsSelectedUpdated.push(
                    easyUpdateFieldsSelectedUpdated.splice(index, 1)[0]
                );
            }

            setSelectedFields(easyUpdateFieldsSelectedUpdated);
        });

        setSameCaseEasyUpdateModel(caseGroupEasyUpdateCopy);
    };

    const handlePreventRemoveValue = (preventedItem: IMultiSelectOption) => {
        if (!preventedItem.readOnly || preventedItem.preventRemoval) {
            handleDeleteFieldClick(preventedItem);
        }
    };

    const handleApplyToAllApplicableAssertionDescriptors = (
        assertionDescriptorGuid: string,
        caseAssertions?: ICaseAssertionModel[] | undefined
    ) => {
        const caseGroupEasyUpdateCopy = cloneDeep(sameCaseEasyUpdateModel);
        const editableCaseModels =
            caseGroupEasyUpdateCopy?.caseModels.filter((cm) => cm.canEdit) ?? [];

        editableCaseModels.forEach((caseCopy) => {
            const existingAssertions = caseCopy.caseAssertions ?? [];

            const assertionsWithoutCurrentGuid = existingAssertions.filter(
                (assertion) => assertion.assertionDescriptorGuid !== assertionDescriptorGuid
            );

            caseCopy.caseAssertions = [...assertionsWithoutCurrentGuid, ...(caseAssertions ?? [])];

            CaseDataCustomLogicHelper.Run(caseCopy, assertionDescriptorsApi.data ?? []);
        });

        setSameCaseEasyUpdateModel(caseGroupEasyUpdateCopy);
    };

    const handleApplyToAllApplicableStaticItems = (zoneGuid: string, staticItemId: number) => {
        const caseGroupEasyUpdateCopy = cloneDeep(sameCaseEasyUpdateModel);

        const caseModelForZone = caseGroupEasyUpdateCopy?.caseModels.find(
            (cm) => cm.organization?.guid === zoneGuid
        );

        const editableCaseModels =
            caseGroupEasyUpdateCopy?.caseModels.filter((cm) => cm.canEdit) ?? [];

        switch (staticItemId) {
            case CaseFileStaticItems.CaseManager.Id:
                editableCaseModels.forEach((cm) => {
                    cm.caseManagerUser = caseModelForZone?.caseManagerUser;
                });
                break;
            case CaseFileStaticItems.AlternateCaseManager.Id:
                editableCaseModels.forEach((cm) => {
                    cm.alternateCaseManagerUser = caseModelForZone?.alternateCaseManagerUser;
                });
                break;
            case CaseFileStaticItems.LocalCounsel.Id: {
                const caseModelForZoneLocalCounsel = caseModelForZone?.defenseCounsels?.find(
                    (dc) => dc.priority === 1
                );

                editableCaseModels.forEach((cm) => {
                    const cmFilteredLocalCounsels = cm.defenseCounsels?.filter(
                        (dc) => dc.priority !== 1
                    );
                    if (caseModelForZoneLocalCounsel) {
                        cmFilteredLocalCounsels?.push(caseModelForZoneLocalCounsel);
                    }
                    cm.defenseCounsels = cmFilteredLocalCounsels;
                });
                break;
            }
            case CaseFileStaticItems.CasePriority.Id:
                editableCaseModels.forEach((cm) => {
                    cm.priority = caseModelForZone?.priority;
                });
                break;
            case CaseFileStaticItems.UniqueCaseID.Id:
                editableCaseModels.forEach((cm) => {
                    cm.uniqueCaseId = caseModelForZone?.uniqueCaseId;
                });
                break;
            case CaseFileStaticItems.CaseNumber.Id:
                editableCaseModels.forEach((cm) => {
                    cm.caseNumber = caseModelForZone?.caseNumber;
                });
                break;
            case CaseFileStaticItems.CaseStatus.Id:
                editableCaseModels.forEach((cm) => {
                    cm.caseStatus = caseModelForZone?.caseStatus;
                });
                break;
            default:
                return null;
        }

        setSameCaseEasyUpdateModel(caseGroupEasyUpdateCopy);
    };

    const handleZoneCaseDataChange = (
        zoneGuid: string,
        newCaseModel: ISameCaseEasyUpdateCaseModel,
        refreshFields: boolean
    ) => {
        if (!sameCaseEasyUpdateModel) return;

        const caseGroupEasyUpdateCopy = cloneDeep(sameCaseEasyUpdateModel);
        const zoneCaseIdx =
            caseGroupEasyUpdateCopy.caseModels.findIndex(
                (cm) => cm.organization?.guid === zoneGuid && cm.guid === newCaseModel.guid
            ) ?? -1;

        if (zoneCaseIdx > -1) {
            caseGroupEasyUpdateCopy?.caseModels.splice(zoneCaseIdx, 1, newCaseModel);
        }

        if (refreshFields) {
            const additionalFields =
                caseGroupEasyUpdateCopy?.caseModels?.flatMap((currentCase) =>
                    getUpdatedEasyUpdateFields(currentCase, selectedFields)
                ) || [];

            const filteredAdditionalFields = additionalFields.filter(
                (field) =>
                    !systemGeneratedIds.includes(field.guid ?? '') &&
                    !selectedFields.some((f) => f.id === field.id || f.guid === field.guid)
            );

            setSelectedFields((prev) => [...prev, ...filteredAdditionalFields]);
        }

        setSameCaseEasyUpdateModel(caseGroupEasyUpdateCopy);
    };

    const handleBackClick = () => {
        if (hasUnsavedChanges) {
            setShowUnsavedChangesOnCancelDialog(true);
        } else {
            if (changesWereMade) {
                props.onComplete?.(sameCaseEasyUpdateModel);
            } else {
                props.onCancel?.();
            }
        }
    };

    const handleUnsavedChangesDialogConfirm = () => {
        props.onCancel?.();
    };

    useEffect(() => {
        props.onSelectedFieldsChange?.(selectedFields);
    }, [selectedFields]);

    useEffect(() => {
        if (!staticItemsApi.isLoading && !assertionDescriptorsApi.isLoading) {
            loadAvailableFields();
        }
    }, [staticItemsApi.data, assertionDescriptorsApi.data]);

    useEffect(() => {
        if (caseGroupEasyUpdateApi.isFetched && assertionDescriptorsApi.isFetched) {
            const data = caseGroupEasyUpdateApi.data;
            let updatedSelectedFields: IMultiSelectOption[] = props.defaultSelectedFields ?? [];

            data?.caseModels?.forEach((cm) => {
                if (!cm) return;
                const caseModelCopy = cloneDeep(cm);

                const filteredSelectedFields = selectedFields.filter(
                    (field) => !systemGeneratedIds.includes(field.guid ?? '')
                );

                const currentFields = getUpdatedEasyUpdateFields(
                    caseModelCopy,
                    filteredSelectedFields
                );

                currentFields.forEach((field) => {
                    const alreadyAdded = updatedSelectedFields.some(
                        (f) => f.id === field.id || f.guid === field.guid
                    );

                    if (!alreadyAdded) {
                        updatedSelectedFields.push(field);
                    }
                });
            });

            setSelectedFields([...updatedSelectedFields]);
            setSameCaseEasyUpdateModel(data);
            setOriginalSameCaseEasyUpdateModel(data);
        }
    }, [caseGroupEasyUpdateApi.isFetched, assertionDescriptorsApi.isFetched]);

    useEffect(() => {
        setChangesWereMade(false);
    }, [sameCaseGuid]);

    useEffect(() => {
        if (mainContainerDivRef?.current) {
            mainContainerDivRef.current.classList.add('has-overlay');
        }

        return () => {
            if (mainContainerDivRef?.current) {
                mainContainerDivRef.current.classList.remove('has-overlay');
            }
        };
    }, [mainContainerDivRef?.current]);

    const cases = sameCaseEasyUpdateModel?.caseModels ?? [];
    const zones = cases.map((caseItem) => caseItem.organization!) ?? [];

    const caseGroupDisplayName = !!sameCaseEasyUpdateModel
        ? `${sameCaseEasyUpdateModel?.caseNumber} | ${sameCaseEasyUpdateModel?.caseName} | ${sameCaseEasyUpdateModel?.jurisdiction}`
        : '';

    const differences = UIHelper.difference(
        cloneDeep(sameCaseEasyUpdateModel),
        cloneDeep(originalSameCaseEasyUpdateModel)
    );

    const adGuidsThatHaveErrorsButAreNotSelected = getAdsWithErrorsThatAreNotVisible(
        assertionDescriptorValidation,
        selectedFields
    );

    const siIdsThatHaveErrorsButAreNotSelected = getSIsWithErrorsThatAreNotVisible(
        staticItemValidation,
        selectedFields
    );

    const nonVisibleADErrors = Object.values(assertionDescriptorValidation).flatMap((zone) =>
        adGuidsThatHaveErrorsButAreNotSelected
            .filter((guid) => guid in zone)
            .map((guid) => zone[guid])
    );

    const nonVisibleSIErrors = Object.values(staticItemValidation).flatMap((zone) =>
        siIdsThatHaveErrorsButAreNotSelected.filter((guid) => guid in zone).map((id) => zone[id])
    );

    const fieldErrorsForHiddenFields = nonVisibleADErrors
        .concat(nonVisibleSIErrors)
        .map((error) => <div>{Object.values(error)}</div>);

    const distinctPageErrors = Array.from(new Set(fieldErrorsForHiddenFields));

    const hasUnsavedChanges = !isEmpty(differences);

    return (
        <>
            <UnsavedChangesBlocker when={hasUnsavedChanges} />
            <DocumentTitle title="Quad - Same Case Easy Update" hideZoneName />
            <PageWithAffixedHeader
                headerComponent={
                    <div className="pt-4">
                        <Stack direction="horizontal" style={{ justifyContent: 'space-between' }}>
                            <Stack direction="horizontal" gap={1} style={{ alignItems: 'center' }}>
                                <h1>Same Case Easy Update</h1>
                                <span className="font-size-xs ps-3">{caseGroupDisplayName}</span>
                            </Stack>
                            <div>
                                {caseGroupEasyUpdateApi.isFetched && hasUnsavedChanges && (
                                    <LoadingButton
                                        onClick={handleSaveClick}
                                        text="Save"
                                        loading={saveMutation.isLoading}
                                        loadingText="Saving..."
                                        buttonCssClass="btn btn-orange"
                                    />
                                )}

                                {caseGroupEasyUpdateApi.isFetched && selectedFields.length > 0 && (
                                    <button className="btn btn-default" onClick={handleClearClick}>
                                        Clear
                                    </button>
                                )}

                                {!props.isRoot && (
                                    <button className="btn btn-blue" onClick={handleBackClick}>
                                        Back
                                    </button>
                                )}
                            </div>
                        </Stack>
                        <div className="pt-3">
                            <FieldSelector
                                options={availableFields}
                                onEasyUpdateFieldsSelected={handleEasyUpdateFieldsSelected}
                                selectedFields={selectedFields}
                                validation={validation}
                                loading={
                                    assertionDescriptorsApi.isFetching || staticItemsApi.isFetching
                                }
                                onPreventRemoveValue={handlePreventRemoveValue}
                            />
                        </div>
                        {distinctPageErrors && (
                            <div className="text-danger mt-3">
                                <div>{distinctPageErrors}</div>
                            </div>
                        )}
                    </div>
                }
            >
                {(caseGroupEasyUpdateApi.isFetching || saveMutation.isLoading) && <Loader />}
                {caseGroupEasyUpdateApi.isError && (
                    <div className="error">
                        Error: {(caseGroupEasyUpdateApi.error as Error).message}
                    </div>
                )}
                <table className="table margin-top-sm" style={{ width: '100%' }}>
                    <thead>
                        <tr>
                            <th>Field</th>
                            <th>Zone</th>
                            <th>Value(s)</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {selectedFields.map((field) => (
                            <SelectedFieldRow
                                assertionDescriptors={assertionDescriptorsApi.data ?? []}
                                cases={cases}
                                field={field}
                                key={field.guid + '|' + field.id}
                                onApplyToAllApplicableAssertionDescriptors={
                                    handleApplyToAllApplicableAssertionDescriptors
                                }
                                onApplyToAllApplicableStaticItems={
                                    handleApplyToAllApplicableStaticItems
                                }
                                onRemoveField={handleDeleteFieldClick}
                                onZoneCaseDataChange={handleZoneCaseDataChange}
                                staticItems={staticItemsApi.data ?? []}
                                zones={zones}
                                zonesWithErrors={zonesWithErrors}
                                validation={{
                                    assertionDescriptorValidation,
                                    staticItemValidation,
                                }}
                            />
                        ))}
                    </tbody>
                </table>
            </PageWithAffixedHeader>

            <CheckmateDialog
                body="This field is part of a group. Deleting it will also delete the rest of the group. Are you sure you wish to delete it?"
                cancelText="Cancel"
                closeButtonClassName="btn btn-default"
                confirmButtonClassName="btn btn-black"
                confirmText="Delete"
                handleClose={() => setAdGroupFieldToDelete(undefined)}
                handleConfirm={() => {
                    handleDeleteFieldClick(adGroupFieldToDelete!, true);
                    setAdGroupFieldToDelete(undefined);
                }}
                isShowingModal={!!adGroupFieldToDelete}
                showBackdrop
                title="Delete Group"
            />

            <CheckmateDialog
                body={'You have unsaved changes. Are you sure you want to proceed?'}
                cancelText={'Cancel'}
                closeButtonClassName="btn btn-default"
                confirmButtonClassName="btn btn-black"
                confirmText={'Continue'}
                handleClose={() => setShowUnsavedChangesOnCancelDialog(false)}
                handleConfirm={handleUnsavedChangesDialogConfirm}
                isShowingModal={showUnsavedChangesOnCancelDialog}
                showBackdrop
                size="sm"
                title={'Unsaved Changes Detected'}
            />
        </>
    );
}
